import * as React from 'react';

import { createPalette } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button, type ButtonProps } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

const AI_PROVIDER_PLUGIN_URLS = {
    claude: 'https://claude.ai/directory/connectors/gitbook-mcp',
    cursor: 'https://cursor.com/marketplace/gitbook',
    codex: 'https://chatgpt.com/apps/gitbook/asdk_app_6a576f075ec4819196c203b7049542be',
} as const;

export type AIProvider = keyof typeof AI_PROVIDER_PLUGIN_URLS;

export const AI_PROVIDERS = Object.keys(AI_PROVIDER_PLUGIN_URLS) as AIProvider[];

type AIProviderLaunchButtonProps = Pick<ButtonProps, 'className' | 'iconOnly' | 'labelStyle'> & {
    provider: AIProvider;
    label?: ButtonProps['label'];
    onLaunch?: (provider: AIProvider) => void;
};

/**
 * Open the GitBook MCP connector listing for an AI provider, so the user can install it.
 */
export function AIProviderLaunchButton(props: AIProviderLaunchButtonProps) {
    const {
        provider,
        label: customLabel,
        iconOnly = false,
        className,
        labelStyle,
        onLaunch,
    } = props;
    const { label: providerLabel, icon, trigger } = getAIProviderLaunchProps(provider);
    const label = customLabel ?? providerLabel;

    return (
        <Button
            kind="secondary"
            size="medium"
            iconOnly={iconOnly}
            label={label}
            labelStyle={labelStyle}
            tooltip={{ label: `Open the GitBook MCP connector for ${label}` }}
            leadingIcon={icon}
            onPress={() => {
                trigger();
                onLaunch?.(provider);
            }}
            className={className}
        />
    );
}

/** Create a palette for launching GitBook in an AI provider or viewing more MCP options. */
export function createAIProviderLaunchPalette(props: { onMoreOptions: () => void }) {
    const providerCommands = AI_PROVIDERS.map((provider) => {
        const { label, icon, trigger } = getAIProviderLaunchProps(provider);

        return {
            key: `mcp.${provider}`,
            title: label,
            icon,
            trigger,
        };
    });

    return createPalette({
        groups: [
            {
                key: 'mcp',
                commands: [
                    ...providerCommands,
                    {
                        key: 'mcp.more_options',
                        title: 'More options',
                        icon: Icon.MoreHorizontal,
                        trigger: props.onMoreOptions,
                    },
                ],
            },
        ],
    });
}

/** Return the shared presentation and launch action for an AI provider's MCP connector. */
export function getAIProviderLaunchProps(provider: AIProvider) {
    const trigger = () => {
        window.open(AI_PROVIDER_PLUGIN_URLS[provider], '_blank', 'noopener,noreferrer');
    };

    switch (provider) {
        case 'claude':
            return {
                label: 'Claude',
                icon: Icon.Claude,
                trigger,
            };
        case 'cursor':
            return {
                label: 'Cursor',
                icon: Icon.Cursor,
                trigger,
            };
        case 'codex':
            // The link opens the GitBook app in ChatGPT, not the Codex coding agent.
            return {
                label: 'ChatGPT',
                icon: Icon.ChatGPT,
                trigger,
            };
        default:
            assertNever(provider);
    }
}
